import {Injectable, OnDestroy}             from '@angular/core';
import {Tenant, User}                      from '@src/app/admin/users/models/user.model';
import {environment}                       from '@src/environments/environment';
import * as Sentry                         from '@sentry/browser';
import * as LaunchDarkly                   from 'launchdarkly-js-client-sdk';
import {LDFlagValue}                       from 'launchdarkly-js-client-sdk';
import {Observable, of}                    from 'rxjs';
import {distinctUntilChanged, shareReplay} from 'rxjs/operators';

@Injectable({
    providedIn: 'root'
})
export class FeatureFlagService implements OnDestroy
{
    public client: LaunchDarkly.LDClient;
    private readonly flagStreams = new Map<string, Observable<LDFlagValue>>();

    constructor()
    {
    }

    public async initialize(user: User, tenant: Tenant): Promise<void>
    {
        if (this.client) {
            await this.client.close();
            this.flagStreams.clear();
        }

        const context = {
            kind: 'multi',
            user: {
                key: `${user.id}`,
                name: user.name,
                email: user.email,
                type: user.type.title,
                anonymous: false,
            },
            tenant: {
                key: `${tenant.id}`,
                name: tenant.name,
                status: tenant.status,
            }
        } as LaunchDarkly.LDMultiKindContext;

        this.client = LaunchDarkly.initialize(environment.launchDarkly.clientId, context, { inspectors: [Sentry.buildLaunchDarklyFlagUsedHandler()] });
    }

    public getFlag(flagKey: string, defaultValue: LDFlagValue = false): Observable<LDFlagValue>
    {
        if (!this.client) {
            console.error('LaunchDarkly client is not initialized. Call initialize() first.');
            return of(defaultValue);
        }

        const cached = this.flagStreams.get(flagKey);
        if (cached) {
            return cached;
        }

        const stream = new Observable<LDFlagValue>((observer) => {
            const emit = (): void => {
                observer.next(this.client.variation(flagKey, defaultValue));
            };

            const changeHandler = (): void => {
                emit();
            };

            this.client.on(`change:${flagKey}`, changeHandler);
            this.client.waitUntilReady().then(emit).catch(() => observer.next(defaultValue));

            return () => {
                this.client.off(`change:${flagKey}`, changeHandler);
            };
        }).pipe(
            distinctUntilChanged(),
            shareReplay({bufferSize: 1, refCount: true}),
        );

        this.flagStreams.set(flagKey, stream);

        return stream;
    }

    public async ngOnDestroy(): Promise<void>
    {
        this.flagStreams.clear();

        if (this.client) {
            await this.client.close();
        }
    }
}
